// file: BlockPlayground.jsx
// Mini playlist composer — pick generated blocks, arrange, and preview sequentially.

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Play, Square, SkipForward, Trash2, Plus, Pin, Tag, ChevronUp, ChevronDown, Music, Search, X } from 'lucide-react';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function blockLabel(block) {
  if (block.type === 'mandatory') return `📌 ${block.poiName} — ${block.topic === 'overview_0' ? 'Overview' : block.topic}`;
  return `🏷 ${block.poiName} — ${block.topic}`;
}

function blockColor(block, active) {
  if (active) return 'bg-green-50 border-green-400';
  if (block.type === 'transition') return 'bg-gray-50 border-gray-200';
  if (block.type === 'orientation') return 'bg-orange-50 border-orange-200';
  if (block.type === 'mandatory') return 'bg-indigo-50 border-indigo-200';
  return 'bg-blue-50 border-blue-200';
}

// ─── Component ────────────────────────────────────────────────────────────────

const BlockPlayground = ({ allBlocks, transitions = [], onDeleteBlock }) => {
  // allBlocks: [{ id, topic, type, transcript, publicUrl, language, poiName, poiId }]
  // transitions: [{ id, transcript, publicUrl, language }]

  const [playlist, setPlaylist] = useState([]);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const stopRef = useRef(false); // flag to abort sequential playback

  // Stop everything on unmount
  useEffect(() => () => {
    stopRef.current = true;
    audioRef.current?.pause();
  }, []);

  // ── Playlist management ───────────────────────────────────────────────────

  const addToPlaylist = (block) => {
    setPlaylist(prev => [...prev, { ...block, _uid: `${block.id}_${Date.now()}` }]);
  };

  const removeFromPlaylist = (uid) => {
    setPlaylist(prev => prev.filter(b => b._uid !== uid));
  };

  const moveItem = (uid, dir) => {
    setPlaylist(prev => {
      const idx = prev.findIndex(b => b._uid === uid);
      if (idx < 0) return prev;
      const next = [...prev];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return prev;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
  };

  const clearPlaylist = () => {
    stopPlayback();
    setPlaylist([]);
  };

  // ── Playback ─────────────────────────────────────────────────────────────

  const stopPlayback = useCallback(() => {
    stopRef.current = true;
    audioRef.current?.pause();
    audioRef.current = null;
    setPlayingIndex(null);
    setIsPlaying(false);
  }, []);

  const playFrom = useCallback((index) => {
    if (stopRef.current || index >= playlist.length) {
      setPlayingIndex(null);
      setIsPlaying(false);
      return;
    }
    const block = playlist[index];
    if (!block?.publicUrl) { playFrom(index + 1); return; }

    if (audioRef.current) { audioRef.current.pause(); }
    const audio = new Audio(block.publicUrl);
    audioRef.current = audio;
    setPlayingIndex(index);
    setIsPlaying(true);

    audio.play().catch(() => playFrom(index + 1));
    audio.onended = () => {
      if (!stopRef.current) playFrom(index + 1);
    };
  }, [playlist]);

  const handlePlay = () => {
    if (playlist.length === 0) return;
    stopRef.current = false;
    playFrom(0);
  };

  const handleStop = () => stopPlayback();

  const handleSkip = () => {
    if (playingIndex == null) return;
    stopRef.current = false;
    playFrom(playingIndex + 1);
  };

  // Individual block preview (outside playlist)
  const [previewingId, setPreviewingId] = useState(null);
  const previewRef = useRef(null);

  const previewBlock = (block) => {
    if (previewRef.current) { previewRef.current.pause(); previewRef.current = null; }
    if (previewingId === block.id) { setPreviewingId(null); return; }
    const audio = new Audio(block.publicUrl);
    previewRef.current = audio;
    setPreviewingId(block.id);
    audio.play().catch(() => setPreviewingId(null));
    audio.onended = () => setPreviewingId(null);
  };

  const [filter, setFilter] = useState('');

  // Filter blocks by keyword/topic search, then group by POI
  const filteredBlocks = useMemo(() => {
    if (!filter.trim()) return allBlocks;
    const q = filter.trim().toLowerCase();
    return allBlocks.filter(b =>
      b.topic?.toLowerCase().includes(q) ||
      b.keywords?.some(k => k.toLowerCase().includes(q)) ||
      b.poiName?.toLowerCase().includes(q)
    );
  }, [allBlocks, filter]);

  const byPoi = filteredBlocks.reduce((acc, b) => {
    const key = b.poiId || b.id;
    if (!acc[key]) acc[key] = { poiName: b.poiName, blocks: [] };
    acc[key].blocks.push(b);
    return acc;
  }, {});

  if (allBlocks.length === 0 && transitions.length === 0) return null;

  return (
    <div className="border border-purple-200 rounded-xl bg-white overflow-hidden">
      {/* Header */}
      <div className="bg-purple-50 border-b border-purple-200 px-4 py-3 flex items-center gap-2">
        <Music className="w-4 h-4 text-purple-600" />
        <span className="text-sm font-semibold text-purple-800">Block Playground</span>
        <span className="text-xs text-purple-500 ml-1">Pick blocks, arrange, and preview</span>
      </div>

      <div className="grid grid-cols-2 divide-x divide-gray-100">

        {/* ── Left: Available blocks ── */}
        <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Available blocks</p>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
            <input
              type="text"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Filter by topic or keyword…"
              className="w-full pl-6 pr-2 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-400"
            />
          </div>
          {transitions.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Transitions</p>
              <div className="space-y-1">
                {transitions.map(t => (
                  <div key={t.id} className="flex items-center gap-2 px-2.5 py-2 rounded-lg border text-xs bg-gray-50 border-gray-200">
                    {t.publicUrl && (
                      <button
                        onClick={() => previewBlock(t)}
                        className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                          previewingId === t.id ? 'bg-red-500 text-white' : 'bg-gray-400 text-white hover:bg-gray-500'
                        }`}
                        title={previewingId === t.id ? 'Stop' : 'Preview'}
                      >
                        {previewingId === t.id ? <Square className="w-2 h-2" /> : <Play className="w-2 h-2" />}
                      </button>
                    )}
                    <span className="flex-1 text-gray-600 italic">{t.transcript}</span>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">{t.language?.toUpperCase()}</span>
                    <button
                      onClick={() => addToPlaylist({ ...t, topic: 'transition', type: 'transition', poiName: 'Transition', poiId: '_transitions' })}
                      className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center bg-gray-200 hover:bg-purple-500 hover:text-white text-gray-600 transition-colors"
                      title="Add to playlist"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Object.entries(byPoi).map(([poiKey, { poiName, blocks }]) => (
            <div key={poiKey}>
              <p className="text-xs font-semibold text-gray-700 mb-1.5 truncate">{poiName}</p>
              <div className="space-y-1">
                {blocks.map(block => (
                  <div
                    key={block.id}
                    className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-xs ${
                      block.type === 'mandatory'
                        ? 'bg-indigo-50 border-indigo-200'
                        : 'bg-blue-50 border-blue-200'
                    }`}
                  >
                    {/* Preview button */}
                    {block.publicUrl && (
                      <button
                        onClick={() => previewBlock(block)}
                        className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                          previewingId === block.id
                            ? 'bg-red-500 text-white'
                            : block.type === 'mandatory'
                              ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                              : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                        title={previewingId === block.id ? 'Stop' : 'Preview'}
                      >
                        {previewingId === block.id
                          ? <Square className="w-2 h-2" />
                          : <Play className="w-2 h-2" />}
                      </button>
                    )}

                    {/* Label + keywords + transcript */}
                    <div className="flex-1 min-w-0">
                      <span className={`font-medium ${
                        block.type === 'mandatory' ? 'text-indigo-700' : 'text-blue-700'
                      }`}>
                        {block.type === 'mandatory'
                          ? <Pin className="inline w-2.5 h-2.5 mr-0.5" />
                          : <Tag className="inline w-2.5 h-2.5 mr-0.5" />}
                        {block.topic}
                      </span>
                      {block.keywords?.length > 0 && (
                        <div className="flex flex-wrap gap-0.5 mt-1">
                          {block.keywords.map(kw => (
                            <span key={kw} className="px-1 py-0 rounded text-gray-500 bg-gray-100 text-[10px]">{kw}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Add to playlist */}
                    <button
                      onClick={() => addToPlaylist(block)}
                      className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center bg-gray-200 hover:bg-purple-500 hover:text-white text-gray-600 transition-colors"
                      title="Add to playlist"
                    >
                      <Plus className="w-3 h-3" />
                    </button>

                    {/* Delete block from library */}
                    {onDeleteBlock && (
                      <button
                        onClick={() => onDeleteBlock(block.poiId, block.id)}
                        className="flex-shrink-0 w-5 h-5 rounded flex items-center justify-center bg-gray-200 hover:bg-red-500 hover:text-white text-gray-400 transition-colors"
                        title="Delete block"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* ── Right: Playlist ── */}
        <div className="p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Playlist ({playlist.length})
            </p>
            {playlist.length > 0 && (
              <button onClick={clearPlaylist} className="text-xs text-gray-400 hover:text-red-500 transition-colors">
                Clear
              </button>
            )}
          </div>

          {/* Playlist items */}
          <div className="flex-1 space-y-1.5 max-h-60 overflow-y-auto">
            {playlist.length === 0 && (
              <p className="text-xs text-gray-400 italic text-center py-6">
                Add blocks from the left →
              </p>
            )}
            {playlist.map((block, i) => {
              const active = playingIndex === i;
              return (
                <div
                  key={block._uid}
                  className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg border text-xs transition-colors ${blockColor(block, active)}`}
                >
                  {/* Playing indicator */}
                  <span className="w-4 text-center flex-shrink-0">
                    {active
                      ? <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      : <span className="text-gray-400">{i + 1}</span>}
                  </span>

                  <div className="flex-1 min-w-0">
                    <span className={`font-medium text-xs ${
                      active ? 'text-green-700'
                      : block.type === 'transition' ? 'text-gray-500'
                      : block.type === 'orientation' ? 'text-orange-700'
                      : block.type === 'mandatory' ? 'text-indigo-700'
                      : 'text-blue-700'
                    }`}>
                      {block.type === 'transition' ? '↔' : block.type === 'orientation' ? '📍' : block.type === 'mandatory' ? '📌' : '🏷'} {block.poiName} · {block.topic}
                    </span>
                    {block.transcript && (
                      <p className="mt-1 text-[11px] text-gray-600 leading-relaxed whitespace-pre-wrap">
                        {block.transcript}
                      </p>
                    )}
                  </div>

                  {/* Reorder + remove */}
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button onClick={() => moveItem(block._uid, -1)} disabled={i === 0} className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-30">
                      <ChevronUp className="w-3 h-3 text-gray-500" />
                    </button>
                    <button onClick={() => moveItem(block._uid, 1)} disabled={i === playlist.length - 1} className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-30">
                      <ChevronDown className="w-3 h-3 text-gray-500" />
                    </button>
                    <button onClick={() => removeFromPlaylist(block._uid)} className="p-0.5 rounded hover:bg-red-100">
                      <Trash2 className="w-3 h-3 text-red-400" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Transcript of currently playing block */}
          {playingIndex != null && playlist[playingIndex]?.transcript && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-2.5">
              <p className="text-xs text-green-600 font-medium mb-1">Now playing</p>
              <p className="text-xs text-green-800 leading-relaxed">
                {playlist[playingIndex].transcript}
              </p>
            </div>
          )}

          {/* Playback controls */}
          <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
            {!isPlaying ? (
              <button
                onClick={handlePlay}
                disabled={playlist.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <Play className="w-3 h-3" /> Play All
              </button>
            ) : (
              <button
                onClick={handleStop}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                <Square className="w-3 h-3" /> Stop
              </button>
            )}
            {isPlaying && (
              <button
                onClick={handleSkip}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <SkipForward className="w-3 h-3" /> Skip
              </button>
            )}
            {playingIndex != null && (
              <span className="text-xs text-gray-400 ml-auto">
                {playingIndex + 1} / {playlist.length}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockPlayground;
